<script lang="ts" setup>
import { ref, watch, onMounted, computed } from 'vue'

import RoomieInput from '@lahaus-roomie/roomie/src/components/RoomieInput/index.vue'
import RoomieLoader from '@lahaus-roomie/roomie/src/components/RoomieLoader/index.vue'
import RoomieChipSelector from '@lahaus-roomie/roomie/src/components/RoomieChipSelector/index.vue'

import IconSend from '@lahaus-roomie/roomie/src/assets/icons/send.svg'

import type { Props } from './types.ts'
import './_index.scss'

import AppBubbleAuthor from '@/components/App/Chat/BubbleAuthor/index.vue'
import AppChatMigratedAttachments from '@/components/App/Chat/Migrated/Attachments/index.vue'
import AppChatRecommendations from '@/components/App/Chat/Recommendations/index.vue'
import AppChatScheduleTurnSelection from '@/components/App/Chat/ScheduleTurnSelection/index.vue'
import AppChatContactMethodSelection from '@/components/App/Chat/ContactMethodSelection/index.vue'
import AppChatReferralSection from '@/components/App/Chat/ReferralSection/index.vue'

import type { AppMessage, UserMessage } from '@/composables/useSamGptCompletions/types'
import { useAppChatStore } from '@/stores/appChatStore'
import { calculateEnterpriseId } from '@/components/App/ChatV2/utils'

const props = defineProps<Props>()

const { t } = useI18n()

const emit = defineEmits(['error'])

const currentMessage = ref('')

const { init, messages, showWritingFeedback, isLoading, handleSendMessage, disableInputField, error, addMessage } = useSamGptCompletions({
  listingIds: [props.listing.code],
  listingName: props.listing.name,
  enterpriseId: calculateEnterpriseId(props.listing)
})

const hasAttachments = (message: AppMessage) => message.attachments?.length

const hasRecommendations = (message: AppMessage) => message.recommendations?.length

const resolveBubblesStyles = (author: string) => author === 'user' ? 'app-chat__author-user' : 'app-chat__author-bot'

const sendMessage = () => {
  handleSendMessage({
    text: currentMessage.value
  })
  currentMessage.value = ''
}

const vScrollBottom = {
  mounted: (el: HTMLElement) => { el.scrollTop = el.scrollHeight },
  updated: (el: HTMLElement) => { el.scrollTop = el.scrollHeight }
}

const vNoAutoComplete = {
  mounted: (el: HTMLElement) => {
    const inputEl = el.getElementsByClassName('roomie-input')[0]
    inputEl.setAttribute('autocomplete', 'off')
  }
}
const appChatStore = useAppChatStore()

const showButtons = (message: AppMessage) => {
  const lastMessage = messages.value[messages.value.length - 1]
  return lastMessage?.buttons?.length && lastMessage === message
}

const handleButtonSelected = (button:string, message: AppMessage) => {
  if (button.toLowerCase().includes('contactar')) {
    addMessage('user', { text: button })
    showWritingFeedback.value = true
    setTimeout(() => {
      showWritingFeedback.value = false
      addMessage('bot', {
        text: '',
        additionalComponents: [
          {
            name: 'AppChatContactMethodSelection',
            props: {
              helperText: t('contactMethodSelectionHelperText'),
              is_in_person_visit_active: message.metadata?.is_in_person_visit_active
            }
          }
        ]
      })
    }, 1000)
  } else {
    handleSendMessage({ text: button })
  }
}

const shouldShowAdditionalComponent = (message: AppMessage) => {
  return message?.additionalComponents?.length
}

const disableScheduleCta = computed(() => {
  const lastMessage = messages.value[messages.value.length - 1]
  return lastMessage?.intent === 'SCHEDULE_CREATED' || lastMessage?.intent === 'VISIT' || lastMessage?.intent === 'AFTER_SCHEDULE'
})

watch(error, () => {
  if (error.value) emit('error')
})

onMounted(async () => {
  await init()
})

</script>

<script lang="ts">
export default {
  name: 'AppChatMigratedBubbles',
  components: {
    AppChatContactMethodSelection,
    AppChatScheduleTurnSelection,
    AppChatReferralSection
  }
}
</script>

<template>
  <div class="overflow-hidden h-full relative">
    <div
      v-scroll-bottom
      class="app-chat__messages-container h-[calc(100%-80px)] p-32 flex flex-col overflow-y-auto overflow-x-hidden">
      <RoomieLoader
        :loading="isLoading"
        size="md"
        class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full" />

      <template
        v-for="message, index in messages"
        :key="index">
        <template v-if="hasAttachments(message) || message.text">
          <AppBubbleAuthor
            :author="message.author"
            :bot-name="botAgentName" />

          <div
            class="app-chat__bubbles text-14 w-fit shadow-[0_0_1px_#0000002f]"
            :class="resolveBubblesStyles(message.author)">
            <div class="flex flex-col gap-16">
              <AppChatMigratedAttachments
                v-if="hasAttachments(message)"
                :listing-name="listing.name"
                :attachments="message.attachments"
                :screen />

              <p
                v-if="message.text"
                v-sanitize="message.text" />
            </div>
          </div>
        </template>

        <template v-if="hasRecommendations(message)">
          <AppChatRecommendations
            class="mt-8"
            :listings="message.recommendations"
            :screen />
        </template>

        <div
          v-if="showButtons(message)"
          class="app-chat__chips-container">
          <RoomieChipSelector
            v-for="(button) in message.buttons"
            :id="`app-chat-message-button-${button.toLowerCase().replace(' ', '-')}`"
            :key="`app-chat-message-button-${button}`"
            :data-lh-id="`${screen}-app-chat-message-button`"
            :is-selected="false"
            @click="handleButtonSelected(button, message)">
            <template #content>
              <p class="text-14 pointer-events-none">
                {{ button }}
              </p>
            </template>
          </RoomieChipSelector>
        </div>

        <template v-if="shouldShowAdditionalComponent(message)">
          <template
            v-for="(comp, i) in message.additionalComponents"
            :key="`additional-component-${comp.name}-${i}`">
            <AppBubbleAuthor
              :author="message.author"
              :bot-name="botAgentName" />

            <component
              :is="comp.name"
              v-bind="comp.props"
              :bot-name="botAgentName"
              :is-schedule-cta-disabled="disableScheduleCta"
              @contact-method-selected="(message: UserMessage) => handleSendMessage(message)"
              @full-turn-selected="(message: UserMessage) => handleSendMessage(message)" />
          </template>
        </template>
      </template>

      <div
        v-if="showWritingFeedback"
        class="text-14 text-primary-600 font-medium">
        <span class="mr-2">
          {{ t('writing') }}
        </span>

        <span
          v-for="index in 3"
          :key="index"
          class="chat-dot mt-24 mr-2 inline-block w-[3px] h-[3px] rounded-full bg-primary-600" />
      </div>
    </div>

    <form
      v-if="!isLoading && !appChatStore.hideChatInputField"
      class="app-chat__send-container w-full p-16 bg-white flex items-center gap-8"
      @submit.prevent="sendMessage">
      <RoomieInput
        id="app-chat-input-send-message"
        v-model="currentMessage"
        v-no-auto-complete
        data-lh-id="app-chat-input-send-message"
        :is-disabled="disableInputField"
        :value="currentMessage" />

      <button
        id="app-chat-send-button"
        data-lh-id="app-chat-send-button"
        :aria-label="t('sendMessageCta')"
        type="submit"
        class="w-24 h-24">
        <IconSend
          class="fill-carbon-600 pointer-events-none"
          :class="{ 'fill-primary-600': currentMessage.length }" />
      </button>
    </form>
  </div>
</template>

<i18n src="./i18n.json" lang="json" />
